import React, { Fragment, useEffect, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  CloseRounded,
  Fullscreen,
  Print,
  ArrowOutward,
  ArrowBackIosRounded,
} from "@mui/icons-material";
import PDFView from "../../../../components/PDFView";
import { useDispatch } from "react-redux";
import { makeStyles } from "tss-react/mui";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import FullViewPDF from "./FullViewPDF";
import { printPlugin } from "@react-pdf-viewer/print";
import "@react-pdf-viewer/print/lib/styles/index.css";
import {
  CurrentPagePlugin,
  DownloadFilePlugin,
} from "../../../../components/PDFView/Toolbar";
import { getFilePlugin } from "@react-pdf-viewer/get-file";
import { getFileIconImage } from "../../../../utils/fileUtils";
import { getFile } from "../../../../core/repo/fileRepo";
import Loader from "../../../../components/Loader";
import { CLICK_SOURCE_FULL_SCREEN } from "../../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";
import CSVViewer from "../../../ViewFile/CSVViewer";

const useStyles = makeStyles()((theme) => ({
  dialog: {
    minHeight: "100%",
  },
  citationCard: {
    boxShadow: "0px 1.592px 3.185px 1.194px rgba(63, 81, 181, 0.12)",
    background:
      "linear-gradient(280deg, #E3DFFC 0.82%, #FEFEFF 50.21%, #FCF2FD 99.6%)",
    borderRadius: 12,
    width: "100%",
  },
  activeCard: {
    border: `1px solid ${theme.palette.accent}`,
    background: theme.palette.paper,
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
}));

function SourceDialog({ selectedSource, onClose }) {
  const theme = useTheme();
  const source = selectedSource;

  return (
    <Grid
      item
      size={{ md: 5 }}
      height={"100%"}
      sx={{
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
      }}>
      {source && <Source source={source} onClose={onClose} />}
    </Grid>
  );
}

function Source({ source, onClose }) {
  const { file } = source;
  return (
    file && <SourceContent file={file} source={source} onClose={onClose} />
  );
}

function SourceContent({ file, source, onClose }) {
  const { classes } = useStyles();
  const theme = useTheme();
  const [isFullViewDialogOpen, setIsFullViewDialogOpen] = useState(false);
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const zoomPluginInstance1 = zoomPlugin();
  const pageNavigationPluginInstance1 = pageNavigationPlugin();
  const zoomPluginInstance2 = zoomPlugin();
  const pageNavigationPluginInstance2 = pageNavigationPlugin();
  const printPluginInstance = printPlugin();
  const getFilePluginInstance = getFilePlugin();
  const [currentPage, setCurrentPage] = useState(null);
  const posthog = usePostHog();
  const isCSV = source["sheet"] && source["sheet"]["csv_url"];

  const handleOpenDialog = () => {
    posthog.capture(CLICK_SOURCE_FULL_SCREEN);
    setIsFullViewDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsFullViewDialogOpen(false);
  };

  return (
    <>
      <Box
        px={2}
        py={1}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}>
        <Box
          display={"flex"}
          alignItems={"center"}
          gap={1}
          sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.2,
              fontWeight: 600,
            }}>
            {file["name"]}
          </Typography>
        </Box>
        <Box display={"flex"} alignItems={"center"}>
          {file["file_url"] && (
            <>
              {!isCSV && (
                <CurrentPagePlugin
                  pageNavigationPluginInstance={pageNavigationPluginInstance1}
                />
              )}
              <IconButton
                className={classes.iconButton}
                onClick={handleOpenDialog}>
                <Fullscreen />
              </IconButton>
              <Divider orientation="vertical" sx={{ mx: 1, height: 24 }} />
            </>
          )}
          <IconButton className={classes.iconButton} onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      <Box px={2} py={1}>
        <Typography variant="body2" noWrap>
          {source["section"]}
        </Typography>
      </Box>
      <Divider />
      <Box
        px={1}
        bgcolor={"#525659"}
        sx={{
          flex: 1,
          overflow: "auto",
          cursor: "pointer",
        }}>
        {isCSV ? (
          <CSVViewer file={{ file_url: source["sheet"]["csv_url"] }} />
        ) : file["file_url"] ? (
          <PDFView
            source={source}
            file={file}
            scale={isXS ? 0.5 : 0.8}
            zoomPluginInstance={zoomPluginInstance1}
            pageNavigationPluginInstance={pageNavigationPluginInstance1}
            printPluginInstance={printPluginInstance}
            getFilePluginInstance={getFilePluginInstance}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        ) : (
          <Typography sx={{ color: "white" }}>
            Error while loading the document
          </Typography>
        )}
      </Box>
      {isFullViewDialogOpen && (
        <FullViewPDF
          isCSV={isCSV}
          csvFileUrl={source["sheet"]?.["csv_url"]}
          currentPage={currentPage}
          source={source}
          zoomPluginInstance={zoomPluginInstance2}
          pageNavigationPluginInstance={pageNavigationPluginInstance2}
          relatedDoc={{ ...file, ...source }}
          printPluginInstance={printPluginInstance}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
}

export function SourcePDF({
  file,
  source,
  handleSourceChange,
  entity = false,
}) {
  const theme = useTheme();
  const [isFullViewDialogOpen, setIsFullViewDialogOpen] = useState(false);
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const zoomPluginInstance1 = zoomPlugin();
  const pageNavigationPluginInstance1 = pageNavigationPlugin();
  const zoomPluginInstance2 = zoomPlugin();
  const pageNavigationPluginInstance2 = pageNavigationPlugin();
  const printPluginInstance = printPlugin();
  const getFilePluginInstance = getFilePlugin();
  const [currentPage, setCurrentPage] = useState(null);

  const handleClose = () => {
    handleSourceChange(null);
  };

  const handleOpenDialog = () => {
    setIsFullViewDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsFullViewDialogOpen(false);
  };

  return (
    <>
      <Box
        height={"100%"}
        sx={{
          borderLeft: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
        }}>
        <Box
          px={2}
          py={1}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}>
          <Typography
            variant="body1"
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.2,
              flex: 1,
              minWidth: 0,
              marginRight: 1,
              fontWeight: 600,
            }}>
            {file["name"]}
          </Typography>
          <Box display={"flex"} alignItems={"center"}>
            {file["file_url"] && (
              <>
                <CurrentPagePlugin
                  pageNavigationPluginInstance={pageNavigationPluginInstance1}
                />
                <IconButton onClick={handleOpenDialog}>
                  <Fullscreen />
                </IconButton>
                {entity && (
                  <>
                    <IconButton onClick={() => printPluginInstance.print()}>
                      <Print />
                    </IconButton>
                    <DownloadFilePlugin
                      getFilePluginInstance={getFilePluginInstance}
                      color={theme.palette.text.secondary}
                    />
                  </>
                )}
                <Divider orientation="vertical" sx={{ mx: 1, height: 24 }} />
              </>
            )}
            <IconButton onClick={handleClose}>
              <CloseRounded />
            </IconButton>
          </Box>
        </Box>
        <Divider />
        <Box px={2} py={1}>
          <Typography variant="body2" noWrap>
            {source["section"]}
          </Typography>
        </Box>
        <Divider />
        <Box
          px={1}
          bgcolor={"#525659"}
          sx={{
            flex: 1,
            overflow: "auto",
            cursor: "pointer",
            // borderBottomLeftRadius: 16,
            // borderBottomRightRadius: 16,
          }}>
          {file["file_url"] ? (
            <PDFView
              source={source}
              file={file}
              scale={isXS ? 0.5 : 0.8}
              zoomPluginInstance={zoomPluginInstance1}
              pageNavigationPluginInstance={pageNavigationPluginInstance1}
              printPluginInstance={printPluginInstance}
              getFilePluginInstance={getFilePluginInstance}
              setCurrentPage={setCurrentPage}
            />
          ) : (
            <Typography sx={{ color: "white" }}>
              Error while loading the document
            </Typography>
          )}
        </Box>
      </Box>
      {isFullViewDialogOpen && (
        <FullViewPDF
          currentPage={currentPage}
          source={source}
          zoomPluginInstance={zoomPluginInstance2}
          pageNavigationPluginInstance={pageNavigationPluginInstance2}
          file={file}
          printPluginInstance={printPluginInstance}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
}

export default SourceDialog;
